import { useCallback, useEffect, useState } from 'react';

// Hide the side menu on a desktop screen for a full-width page (Gary, 12 Oct 2026).
// Same behaviour as the Praxa portals (Baobab, WOT): a menu button at the left of the
// top bar, Ctrl+B or Cmd+B as a shortcut, and the choice is remembered. Phones keep
// their own slide-out menu; this only acts above the mobile breakpoint.
const STORAGE_KEY = 'sentinel-sidebar-hidden';
const SHORTCUT_KEY = 'b';

function readStored(): boolean {
  try {
    return localStorage.getItem(STORAGE_KEY) === '1';
  } catch {
    return false;
  }
}

export function useSidebarHidden(): { hidden: boolean; toggle: () => void } {
  const [hidden, setHidden] = useState<boolean>(readStored);

  const toggle = useCallback(() => {
    setHidden(prev => {
      const next = !prev;
      try {
        localStorage.setItem(STORAGE_KEY, next ? '1' : '0');
      } catch {
        // Private window or blocked storage: the toggle still works for this visit.
      }
      return next;
    });
  }, []);

  useEffect(() => {
    const onKey = (event: KeyboardEvent) => {
      // Leave Ctrl+B alone while typing: in the email editors it means bold.
      const target = event.target as HTMLElement | null;
      if (target && (target.isContentEditable || ['INPUT', 'TEXTAREA', 'SELECT'].includes(target.tagName))) return;
      if (event.key.toLowerCase() === SHORTCUT_KEY && (event.metaKey || event.ctrlKey) && !event.altKey && !event.shiftKey) {
        event.preventDefault();
        toggle();
      }
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [toggle]);

  return { hidden, toggle };
}
